import React, {useEffect, useState} from 'react';
import {Control, Controller} from 'react-hook-form';
import LinkDropdown from '@components/system/LinkDropDown/LinkDropdown';
import {DATE_RANGE_OPTIONS} from '@shared/constants/Mapping';
import {makeStyles} from '@material-ui/core';
import moment from 'moment';
import HelperUtils from '@shared/utils/HelperUtils';
import {useTranslation} from 'react-i18next';
import {FormField} from '@components/system/FormField/FormField';

interface PharmacyClaimsDateRangeFilterFieldProps {
  label?: string;
  fromFiled: {
    name: string;
    defaultValue?: any;
    placeholder: string;
  };
  toField: {
    name: string;
    defaultValue?: any;
    placeholder: string;
  };
  control: Control;
  onUpdateDateRange: (
    params: {from: string; to: string},
    selectedDateRangeLabelObject?: any
  ) => void;
  defaultSelectedRangeKey?: string;
  // react-hook-form's UseFormGetValues may not be exported in this project's version
  // so type as any to maintain compatibility
  getValues?: any;
}

const useStyles = makeStyles(() => ({
  dateFieldWrapper: {
    display: 'flex',
    alignItems: 'flex-end',
    width: '100%',
    gap: '1rem'
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    justifyContent: 'space-between'
  },
  dateField: {
    flex: 1,
    width: '13rem',
    '& .MuiOutlinedInput-adornedEnd': {
      paddingRight: '8px'
    }
  }
}));

const PharmacyClaimsDateRangeFilterField = (
  props: PharmacyClaimsDateRangeFilterFieldProps
): JSX.Element => {
  const {
    fromFiled,
    toField,
    control,
    onUpdateDateRange,
    defaultSelectedRangeKey,
    getValues
  } = props;
  const [selectedRange, setSelectedRange] = useState('');
  const classes = useStyles();
  const {t} = useTranslation();
  // Effect to sync the state if the defaultSelectedRange changes
  useEffect(() => {
    if (defaultSelectedRangeKey) {
      const dateRangeItem = DATE_RANGE_OPTIONS.find(
        i => i.key === defaultSelectedRangeKey
      );
      if (dateRangeItem) {
        handleClickOnDateRange(dateRangeItem);
        setSelectedRange(dateRangeItem.label);
      }
    }
  }, [defaultSelectedRangeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClickOnDateRange = (item: {label: string; key: string}) => {
    if (item.key === 'TODAY') {
      const today = moment();
      onUpdateDateRange(
        {
          from: HelperUtils.formatDisplayDate(today),
          to: HelperUtils.formatDisplayDate(today)
        },
        item
      ); // Pass the selected item here
    } else if (item.key === 'YESTERDAY') {
      const yesterday = moment().subtract(1, 'days');
      onUpdateDateRange(
        {
          from: HelperUtils.formatDisplayDate(yesterday),
          to: HelperUtils.formatDisplayDate(yesterday)
        },
        item
      );
    } else if (item.key === 'THIS_WEEK') {
      const startOfWeek = moment().startOf('week');
      const endOfWeek = moment().endOf('week');
      onUpdateDateRange(
        {
          from: HelperUtils.formatDisplayDate(startOfWeek),
          to: HelperUtils.formatDisplayDate(endOfWeek)
        },
        item
      );
    } else if (item.key === 'LAST_WEEK') {
      const lastWeekStart = moment().subtract(1, 'weeks').startOf('week');
      const lastWeekEnd = moment(lastWeekStart).endOf('week');
      onUpdateDateRange(
        {
          from: HelperUtils.formatDisplayDate(lastWeekStart),
          to: HelperUtils.formatDisplayDate(lastWeekEnd)
        },
        item
      );
    } else if (item.key === 'PAST_TWO_WEEKS') {
      const startOfWeek = moment().subtract(2, 'weeks');
      const endOfWeek = moment().subtract(1, 'weeks').endOf('week');
      onUpdateDateRange(
        {
          from: HelperUtils.formatDisplayDate(startOfWeek),
          to: HelperUtils.formatDisplayDate(endOfWeek)
        },
        item
      );
    } else if (item.key === 'THIS_MONTH') {
      const startOfMonth = moment().startOf('month');
      const endOfMonth = moment().endOf('month');
      onUpdateDateRange(
        {
          from: HelperUtils.formatDisplayDate(startOfMonth),
          to: HelperUtils.formatDisplayDate(endOfMonth)
        },
        item
      );
    } else if (item.key === 'LAST_MONTH') {
      const lastMonth = moment().subtract(1, 'months');
      const startOfMonth = moment(lastMonth).startOf('month');
      const endOfMonth = moment(lastMonth).endOf('month');
      onUpdateDateRange(
        {
          from: HelperUtils.formatDisplayDate(startOfMonth),
          to: HelperUtils.formatDisplayDate(endOfMonth)
        },
        item
      );
    } else if (item.key === 'THIS_YEAR') {
      const start = moment().startOf('year');
      const end = moment().endOf('year');
      onUpdateDateRange(
        {
          from: HelperUtils.formatDisplayDate(start),
          to: HelperUtils.formatDisplayDate(end)
        },
        item
      );
    } else if (item.key === 'LAST_YEAR') {
      const lastYear = moment().subtract(1, 'years');
      const start = moment(lastYear).startOf('year');
      const end = moment(lastYear).endOf('year');
      onUpdateDateRange(
        {
          from: HelperUtils.formatDisplayDate(start),
          to: HelperUtils.formatDisplayDate(end)
        },
        item
      );
    }
    setSelectedRange(item.label);
  };

  return (
    <div className="date-range-input-field">
      <div className={classes.dateFieldWrapper}>
        <div className={classes.dateField}>
          <Controller
            control={control}
            name={fromFiled.name}
            defaultValue={fromFiled.defaultValue || null}
            render={inputProps => (
              <FormField
                type="date"
                {...inputProps}
                onChange={(value: string) => {
                  inputProps.onChange(value);
                  setSelectedRange('');
                }}
                label={''}
                placeholder={fromFiled.placeholder}
              />
            )}
          />
        </div>
        <div className={classes.dateField}>
          <Controller
            control={control}
            name={toField.name}
            defaultValue={toField.defaultValue || null}
            render={inputProps => (
              <FormField
                type="date"
                {...inputProps}
                onChange={(value: string) => {
                  inputProps.onChange(value);
                  setSelectedRange('');
                  if (!getValues) return;
                  const fromVal = getValues(fromFiled.name);
                  if (fromVal && value) {
                    onUpdateDateRange(
                      {from: fromVal, to: value},
                      {label: t('CLAIMS.CUSTOM_DATE_RANGE'), key: 'CUSTOM'}
                    );
                  }
                }}
                label={''}
                placeholder={toField.placeholder}
              />
            )}
          />
        </div>
        <p className={classes.label}>
          {/* <p>{label}</p> */}
          <LinkDropdown
            title={selectedRange || t('FORM.SELECT_ONE_LABEL')}
            listItems={DATE_RANGE_OPTIONS}
            handleItemClick={item => handleClickOnDateRange(item)}
            // position=""
          />
        </p>
      </div>
    </div>
  );
};

export default PharmacyClaimsDateRangeFilterField;
